import { DatadogProvider } from '@datadog/openfeature-browser';
import { OpenFeature } from '@openfeature/web-sdk';

/*
 * Temporary Datadog Product Analytics + Experiments trial utility.
 *
 * This helper is intentionally scoped for the current trial use case (IYP modal
 * gating on web/mobile web), including default Datadog client config and a
 * minimal context builder used by active experiment targeting.
 *
 * If the trial becomes permanent, refactor this into a more general application
 * integration:
 * - move product/app config to an explicit runtime config source,
 * - expand context contracts for broader feature usage,
 * - and centralize lifecycle/ownership for initialization and updates.
 */
const REQUIRED_FIELDS = ['applicationId', 'clientToken', 'env'];
const getDefaultFeatureFlagsEnv = () => {
  if (typeof ENV !== 'string') {
    return 'development';
  }
  return ENV === 'prod' ? 'production' : ENV;
};
const DATADOG_FEATURE_FLAGS_DEFAULT_CONFIG = {
  applicationId: '2bbbad6e-67cb-4049-a31e-8384e15fbd90',
  clientToken: 'pub6398a4c636ff6803345df9156eedeefa',
  site: 'datadoghq.com',
  env: getDefaultFeatureFlagsEnv(),
};

const isPrimitive = (value) => ['string', 'number', 'boolean'].includes(typeof value);

const sanitizeEvaluationContext = (context = {}) => Object.entries(context).reduce((result, [key, value]) => {
  if (isPrimitive(value)) {
    result[key] = value;
  }
  return result;
}, {});

const getIsUSFromState = (state = {}) => {
  const isNonUS = state?.user?.userLocation?.isNonUS;
  if (typeof isNonUS === 'boolean') {
    return !isNonUS;
  }

  const countryCode = state?.ipdata?.countryCode || state?.user?.userLocation?.countryCode;
  if (typeof countryCode === 'string') {
    return countryCode.toLowerCase() === 'us';
  }

  return false;
};

const buildDatadogFeatureFlagsContext = ({ baseContext = {}, state } = {}) => ({
  ...baseContext,
  is_us: getIsUSFromState(state),
});

const assertRequiredConfig = (config) => {
  REQUIRED_FIELDS.forEach((field) => {
    if (!config[field]) {
      throw new Error(`initDatadogFeatureFlags missing required field: ${field}`);
    }
  });
};

const initDatadogFeatureFlags = async ({
  applicationId = DATADOG_FEATURE_FLAGS_DEFAULT_CONFIG.applicationId,
  clientToken = DATADOG_FEATURE_FLAGS_DEFAULT_CONFIG.clientToken,
  site = DATADOG_FEATURE_FLAGS_DEFAULT_CONFIG.site,
  env = DATADOG_FEATURE_FLAGS_DEFAULT_CONFIG.env,
  context = {},
  providerOptions = {},
}) => {
  const config = {
    applicationId,
    clientToken,
    site,
    env,
  };
  assertRequiredConfig(config);

  const provider = new DatadogProvider({
    ...config,
    ...providerOptions,
  });
  const evaluationContext = sanitizeEvaluationContext(context);

  await OpenFeature.setProviderAndWait(provider, evaluationContext);
  return OpenFeature.getClient();
};

const setDatadogFeatureFlagsContext = async (context = {}) => {
  await OpenFeature.setContext(sanitizeEvaluationContext(context));
};

const getDatadogFeatureFlagsClient = () => OpenFeature.getClient();

const closeDatadogFeatureFlags = async () => {
  await OpenFeature.close();
};

export {
  buildDatadogFeatureFlagsContext,
  closeDatadogFeatureFlags,
  getDatadogFeatureFlagsClient,
  getIsUSFromState,
  initDatadogFeatureFlags,
  sanitizeEvaluationContext,
  setDatadogFeatureFlagsContext,
};
